import React from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Typography,
	Alert,
	List,
	ListItem,
	Stack,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import "./ImportSettings.css";
import { ImportTableColumn } from "../../../utilities/types";
import { CustomAutocomplete } from "../../helpers";
import { useImportSettingsViewModel } from "./ImportSettings.vm";
import { columnDisplayNames, importFlowOptions } from "./ImportSettings.types";

/**
 * View component for ImportSettings
 * Pure presentational component that consumes the ViewModel
 */
export const ImportSettings: React.FC = () => {
	const vm = useImportSettingsViewModel();

	// Permission check
	if (!vm.isSuperUser) {
		return (
			<Box className="permission-denied">
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	// Loading state
	if (vm.loading) {
		return (
			<Box className="loading-state">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<div className="submit-page">
			<Grid container spacing={1} sx={{ maxWidth: "md", margin: "auto", width: "100%" }}>
				{/* Add spacing component here */}
				<Grid size={12} sx={{ height: "20px" }}></Grid>

				{/* Company Selection */}
				<Grid size={12}>
					<Card className="company-card">
						<CardContent>
							<CustomAutocomplete
								label="Select Company"
								options={vm.companies}
								selectedOption={vm.selectedCompany}
								onChange={vm.onSelectCompany}
								loading={vm.loading}
								optionLabelField="name"
							/>
						</CardContent>
					</Card>
				</Grid>

				{/* Copy Settings Section */}
				{vm.selectedCompany && (
					<Grid size={12}>
						<Card className="copy-settings-card">
							<CardContent>
								<Typography variant="subtitle1" gutterBottom>
									Copy Settings From Another Company
								</Typography>
								<Grid container spacing={2} alignItems="center">
									<Grid size={{ xs: 12, md: 8 }}>
										<CustomAutocomplete
											label="Source Company"
											options={vm.companies.filter(
												(c) => c.id !== vm.selectedCompany?.id && !!c.portalSettings?.importTableColumns,
											)}
											selectedOption={vm.sourceCompany}
											onChange={vm.onSelectSourceCompany}
											loading={vm.loading}
											optionLabelField="name"
										/>
									</Grid>
									<Grid size={{ xs: 12, md: 4 }}>
										<Button
											variant="outlined"
											fullWidth
											onClick={vm.onCopySettings}
											disabled={!vm.sourceCompany || vm.copying}
											startIcon={vm.copying ? <CircularProgress size={20} /> : null}
										>
											{vm.copying ? "Copying..." : "Copy Settings"}
										</Button>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				)}

				{/* Import Flow Options */}
				{vm.selectedCompany && (
					<Grid size={12}>
						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Import Options
								</Typography>
								<Grid container spacing={0}>
									{Object.entries(importFlowOptions).map(([key, displayName]) => (
										<Grid size={{ xs: 12, sm: 12 }} key={key}>
											<FormControlLabel
												control={
													<Checkbox
														checked={vm.importFlowSettings[key] || false}
														onChange={(e) => vm.onImportFlowChange(key, e.target.checked)}
													/>
												}
												label={displayName}
											/>
										</Grid>
									))}
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				)}

				{/* Column Settings Section */}
				{vm.selectedCompany && (
					<Grid size={12}>
						<Card className="column-settings-card">
							<CardContent>
								<Box className="header-section">
									<Typography variant="h6">Visible/Required</Typography>
									<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} className="header-controls">
										<FormControlLabel
											control={
												<Checkbox
													checked={vm.allNonHardcodedVisible}
													onChange={(e) => vm.onSetAllVisible(e.target.checked)}
												/>
											}
											label="All Visible"
										/>
										<FormControlLabel
											control={
												<Checkbox
													checked={vm.allVisibleNonHardcodedRequired}
													onChange={(e) => vm.onSetAllRequired(e.target.checked)}
												/>
											}
											label="All Required"
										/>
									</Stack>
								</Box>
								<List className="column-list">
									{Object.entries(columnDisplayNames).map(([key, displayName]) => {
										const column = key as ImportTableColumn;
										const hardcodedLabel = vm.getHardcodedLabel(column);

										return (
											<ListItem key={key} divider className="column-item">
												<Box className="column-name">
													<Typography>{displayName}</Typography>
													{hardcodedLabel && (
														<Typography variant="caption" className="hardcoded-label">
															{hardcodedLabel}
														</Typography>
													)}
												</Box>
												<Stack
													direction={{ xs: "column", sm: "row" }}
													spacing={{ xs: 1, sm: 2 }}
													className="column-controls"
												>
													<FormControlLabel
														control={
															<Checkbox
																checked={vm.columnSettings[key]?.visible || false}
																onChange={(e) => vm.onColumnChange(column, "visible", e.target.checked)}
																disabled={vm.isVisibleDisabled(column)}
															/>
														}
														label="Visible"
													/>
													<FormControlLabel
														control={
															<Checkbox
																checked={vm.columnSettings[key]?.required || false}
																onChange={(e) => vm.onColumnChange(column, "required", e.target.checked)}
																disabled={vm.isRequiredDisabled(column)}
															/>
														}
														label="Required"
													/>
												</Stack>
											</ListItem>
										);
									})}
								</List>
								<Box className="button-container">
									<Button variant="outlined" onClick={vm.onResetToDefaults}>
										Reset to Defaults
									</Button>
									{vm.hasChanges && (
										<Button variant="outlined" onClick={vm.onCancel}>
											Cancel
										</Button>
									)}
									<Button
										variant="contained"
										color="primary"
										onClick={vm.onSave}
										disabled={vm.saving || !vm.hasChanges}
									>
										{vm.saving ? "Saving..." : "Save"}
									</Button>
								</Box>
							</CardContent>
						</Card>
					</Grid>
				)}
			</Grid>
		</div>
	);
};
